<!--
 * @Author: AI Assistant
 * @Date: 2026-01-15
 * @Description: PWA 添加到桌面引导弹窗组件
 * @FilePath: /weryai-web/components/Tool/PwaGuideModal.vue
-->

<template>
    <div
        v-if="visible"
        class="fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-black/70"
        @click.self="close"
    >
        <div class="pwa-guide-content w-[90vw] md:w-[500px] max-w-[500px]">
            <!-- 标题和关闭按钮 -->
            <div class="flex items-center justify-center relative w-full">
                <span class="text-xl font-medium text-white">{{ t('signIn.pwaGuideTitle') }}</span>
                <img
                    src="@/assets/images/common/_close.webp"
                    alt="close"
                    class="w-6 h-6 absolute right-0 top-0 cursor-pointer hover:scale-105 hover:rotate-180 transition-all duration-300"
                    @click="close"
                />
            </div>

            <!-- 说明文字 -->
            <div class="text-text-adad text-sm text-center mt-3">
                {{ t('signIn.pwaGuideDesc') }}
            </div>

            <!-- 引导步骤区域 -->
            <div v-if="browserType !== 'other'" class="mt-5 space-y-4">
                <!-- 步骤1 -->
                <div class="pwa-step-container">
                    <div class="pwa-step-header">
                        <span class="pwa-step-number">1</span>
                        <span class="pwa-step-text">{{ stepTexts[0] }}</span>
                    </div>
                    <img 
                        :src="guideImages[0]" 
                        alt="Step 1" 
                        class="pwa-step-image"
                    />
                </div>

                <!-- 步骤2 -->
                <div class="pwa-step-container">
                    <div class="pwa-step-header">
                        <span class="pwa-step-number">2</span>
                        <span class="pwa-step-text">{{ stepTexts[1] }}</span>
                    </div>
                    <img 
                        :src="guideImages[1]" 
                        alt="Step 2" 
                        class="pwa-step-image"
                    />
                </div>
            </div>

            <!-- 不支持的浏览器提示 -->
            <div v-else class="mt-5 p-4 bg-[#1C1C20] rounded-lg text-center">
                <svg class="w-12 h-12 mx-auto mb-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p class="text-text-adad text-sm">{{ t('signIn.browserNotSupported') }}</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
const { t } = useI18n()

// Props
const props = defineProps<{
    visible: boolean
}>()

// Emits
const emit = defineEmits<{
    (e: 'close'): void
}>()

// 浏览器类型
type BrowserType = 'chrome' | 'edge' | 'safari' | 'other'

// 检测浏览器类型
const browserType = computed<BrowserType>(() => {
    if (!import.meta.client) return 'other'
    
    const ua = navigator.userAgent
    
    // Edge 检测 (必须先于 Chrome，因为 Edge 也包含 Chrome)
    if (ua.includes('Edg')) {
        return 'edge'
    }
    
    // Chrome 检测 (排除 Edge 和 Opera)
    if (ua.includes('Chrome') && !ua.includes('Edg') && !ua.includes('OPR')) {
        return 'chrome'
    }
    
    // Safari 检测 (排除 Chrome 系)
    if (ua.includes('Safari') && !ua.includes('Chrome')) {
        return 'safari'
    }
    
    return 'other'
})

// 根据浏览器类型返回对应的引导图片
const guideImages = computed<string[]>(() => {
    const imageMap: Record<BrowserType, string[]> = {
        chrome: [
            'https://static.weryai.com/cms/cdesktop1.webp',
            'https://static.weryai.com/cms/cdesktop2.webp'
        ],
        edge: [
            'https://static.weryai.com/cms/medesktop1.webp',
            'https://static.weryai.com/cms/medesktop2.webp'
        ],
        safari: [
            'https://static.weryai.com/cms/sadesktop1.webp',
            'https://static.weryai.com/cms/sadesktop2.webp'
        ],
        other: []
    }
    
    return imageMap[browserType.value]
})

// 根据浏览器类型返回对应的步骤文案
const stepTexts = computed<string[]>(() => {
    const textMap: Record<BrowserType, string[]> = {
        chrome: [
            t('signIn.pwaStepChrome1'),
            t('signIn.pwaStepChrome2')
        ],
        edge: [
            t('signIn.pwaStepEdge1'),
            t('signIn.pwaStepEdge2')
        ],
        safari: [
            t('signIn.pwaStepSafari1'),
            t('signIn.pwaStepSafari2')
        ],
        other: []
    }
    
    return textMap[browserType.value]
})

// 关闭弹窗
const close = () => {
    emit('close')
}
</script>

<style scoped>
.pwa-guide-content {
    max-height: 85vh;
    background: url('https://static.weryai.com/growth/image/b449340ab3337479db32226d65e29986.webp')
        no-repeat top center;
    background-size: 100% auto;
    background-color: #0d0e12;
    overflow-y: auto;
    padding: 24px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 步骤容器 */
.pwa-step-container {
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.10);
    padding: 16px;
}

/* 步骤头部 */
.pwa-step-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    margin-bottom: 12px;
}

/* 步骤编号 */
.pwa-step-number {
    border-radius: 5px;
    background: #A3E635;
    color: #000;
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    width: 26px;
    height: 26px;
    min-width: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* 步骤文案 */
.pwa-step-text {
    color: #E4E4E7;
    font-family: "PingFang SC", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
}

/* 步骤图片 */
.pwa-step-image {
    width: 100%;
    border-radius: 10px;
}

/* 自定义滚动条 */
.pwa-guide-content::-webkit-scrollbar {
    width: 6px;
}

.pwa-guide-content::-webkit-scrollbar-track {
    background: transparent;
}

.pwa-guide-content::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
}

.pwa-guide-content::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
}
</style>
